import { useMemo, useState, useEffect, useRef, useContext } from 'react';
import { Button, Subtitle2, tokens } from '@fluentui/react-components';
import { CommentAddRegular, Dismiss24Regular } from '@fluentui/react-icons';

import NewComment from './NewComment';
import SingleComment from './SingleComment';
import { customBrandRamp } from '../../../app/ThemeProvider';
import useCreateSelection from '../hooks/useCreateSelection.hook';
import { COMMENT_RESOLUTION_TABS, COMMENTS_HTML_ATTRIBUTES, NEW_COMMENT_ID } from '../constants';
import ResolutionTabs from './buttons/ResolutionTabs';
import { showCommentForVersion } from '../helpers/showCommentForVersion.helper';

import { TDocument, TDraft, TVersion, useScrollContent, DocumentContext, SIDEBAR_WIDTH_PX } from '@modules/documents';
import { TFile, TFileVersion } from '@modules/files';
import { getVersionId, TResolvedItemsMap, useItemScreenStore } from '@modules/items';
import { TPermissions } from '@modules/access';

import { CustomScrollbar, TScrollbarElem } from '@shared/components';
import { sortByDateDesc } from '@shared/helpers';
import { useScrollContainer } from '@shared/hooks';
import useCommentStore from '../stores/useCommentStore';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '@modules/inlineBlocks';
import useGetSelectionInfo from '../hooks/useGetSelectionInfo.hook';
import useCommentOccurrenceOrder from '../hooks/useCommentOccurrenceOrder.hook';

type TProps = {
  item: TDocument | TFile | TInlineBlock;
  permissions: TPermissions;
  version: TVersion | TFileVersion | TInlineBlockVersion | TDraft | TInlineBlockDraft;
  // Only needed for impact comments:
  resolvedItems: TResolvedItemsMap;
};

const AllComments = ({ item, permissions, version, resolvedItems }: TProps) => {
  const { setScreenMode } = useItemScreenStore();
  const { setSelectedCommentId, selectedCommentId } = useCommentStore();
  const documentContext = useContext(DocumentContext);
  const occurrenceOrder = useCommentOccurrenceOrder(documentContext?.editor, documentContext?.isContentReady);

  // State for the "New Comment" form
  const [isNewCommentOpen, setIsNewCommentOpen] = useState(false);

  // The "Unresolved" vs. "Resolved" tab
  const [selectedResolutionTab, setSelectedResolutionTab] = useState<string>(COMMENT_RESOLUTION_TABS[0].value);

  const scrollContent = useScrollContent();

  // -------------------------------
  // Auto-select the correct resolution tab on comment selection
  // -------------------------------
  useEffect(() => {
    if (selectedCommentId) {
      const comment = item.comments.find((c) => c._id === selectedCommentId);
      const isResolved = comment && comment.resolvedDate;
      // Open the correct tab if the comment is resolved/unresolved
      setSelectedResolutionTab(isResolved ? 'resolved-tab' : 'unresolved-tab');
    }
  }, [selectedCommentId]);

  // We'll parse the version in order to compare with comment.versionCreated/resolved
  const versionId = getVersionId(version); // e.g. "5.1" or "draft"

  // -------------------------------
  /**
   * Filter logic:
   *  1. Only keep comments that match the current resolution tab
   *     - "resolved-tab" => has "resolvedDate" (or versionResolved)
   *     - "unresolved-tab" => does not have "resolvedDate"
   *  2. Then only keep comments that pass the "versionMode" logic
   *     - see `matchVersionLogic` for details
   *  3. Sort by first selection occurrence, then by the tab's date for comments without a selection.
   */
  // -------------------------------
  const filteredAndSortedComments = useMemo(() => {
    // 1) Filter by "unresolved" vs. "resolved"
    const filteredByTab = item.comments.filter((comment) => {
      if (selectedResolutionTab === 'resolved-tab') {
        return !!comment.resolvedDate;
      } else if (selectedResolutionTab === 'unresolved-tab') {
        return !comment.resolvedDate;
      }
      return true;
    });

    // 2) Filter by version logic
    const filteredByVersion = filteredByTab.filter((comment) => {
      return showCommentForVersion(comment, versionId);
    });

    // 3) Sort
    filteredByVersion.sort((a, b) => {
      const rankA = occurrenceOrder.get(a._id) ?? Infinity;
      const rankB = occurrenceOrder.get(b._id) ?? Infinity;
      if (rankA !== rankB) return rankA - rankB;
      const dateOrder =
        selectedResolutionTab === 'unresolved-tab'
          ? sortByDateDesc(a.createdDate, b.createdDate)
          : sortByDateDesc(a.resolvedDate, b.resolvedDate);
      return dateOrder || a._id.localeCompare(b._id);
    });

    return filteredByVersion;
  }, [item.comments, selectedResolutionTab, versionId, occurrenceOrder]);

  // -------------------------------
  // Handle the "New Comment" button click
  // -------------------------------
  const createSelection = useCreateSelection();

  const handleNewCommentClick = () => {
    if (!isNewCommentOpen) {
      // createSelection handles bound docs internally: highlight inside a field, else a general comment.
      if (item.type === 'DOCUMENT' && !createSelection()) return;

      setIsNewCommentOpen(true);
      setSelectedCommentId(NEW_COMMENT_ID);

      // Open the "Unresolved" tab by default
      setSelectedResolutionTab('unresolved-tab');
    }
  };

  // -------------------------------
  // Handle the "Close" button click
  // -------------------------------
  const handleCloseSidebar = () => {
    setScreenMode();
    setSelectedCommentId(undefined);
  };

  // -------------------------------
  // When "selectedCommentId" changes:
  //  - scroll to that comment in the sidebar
  //  - scroll to that comment in the editor
  // -------------------------------
  const commentContainerRef = useRef<TScrollbarElem | null>(null);
  const refHasNavigated = useRef(false);
  const variantFolder = documentContext?.variant?.folder;

  const scrollContainer = useScrollContainer();
  const getSelectionInfo = useGetSelectionInfo();

  useEffect(() => {
    refHasNavigated.current = false;
  }, [selectedCommentId, item.type, item._id, versionId, variantFolder]);

  useEffect(() => {
    // Saving flips readiness and comment updates replace the list. Neither requests navigation again.
    if (!selectedCommentId || refHasNavigated.current) return;

    const selectionInfo = getSelectionInfo();
    const selectedCommentHasSelection =
      selectedCommentId === NEW_COMMENT_ID
        ? Boolean(selectionInfo.markSelection || selectionInfo.nodeSelection || selectionInfo.fieldSelection)
        : Boolean(item.comments.find((c) => c._id === selectedCommentId)?.hasSelection);

    // 1) Scroll to the comment in the sidebar
    scrollContainer({
      containerRef: commentContainerRef,
      attribute: COMMENTS_HTML_ATTRIBUTES.COMMENT_CONTAINER_ID,
      value: selectedCommentId,
    });

    // New comment with no selection → skip editor scroll
    if (!selectedCommentHasSelection) return;
    if (documentContext && !documentContext.isContentReady) return;

    // 2) Scroll to the comment selection when changing the selection
    refHasNavigated.current = true;
    scrollContent({
      attribute: COMMENTS_HTML_ATTRIBUTES.COMMENT_ID,
      value: selectedCommentId,
    });
  }, [
    selectedCommentId,
    item.comments,
    documentContext?.isContentReady,
    item.type,
    item._id,
    versionId,
    variantFolder,
  ]);

  // We need to inject a style attribute, as there is no addNodeView() method for Tiptap Marks:
  const dynamicStyle = selectedCommentId
    ? `
    selection-mark[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${selectedCommentId}"] {
      background-color: ${customBrandRamp[150]} !important;
    }
    selection-mark[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${NEW_COMMENT_ID}"] {
      box-shadow:
        inset 2px 0 0 ${tokens.colorBrandForeground1},
        inset -2px 0 0 ${tokens.colorBrandForeground1};
    }
    selection-node[${COMMENTS_HTML_ATTRIBUTES.COMMENT_ID}="${selectedCommentId}"] {
      background-color: ${customBrandRamp[150]} !important;
      border-top: 2px solid ${tokens.colorBrandForeground1} !important;
      border-bottom: 2px solid ${tokens.colorBrandForeground1} !important;
    }
  `
    : '';

  return (
    <div className="flex flex-col h-full" style={{ width: `${SIDEBAR_WIDTH_PX}px`, minWidth: `${SIDEBAR_WIDTH_PX}px` }}>
      <style>{dynamicStyle}</style>
      <div className="pt-3 pl-4 pb-2 pr-3">
        <div className="flex justify-between items-center mb-2">
          <Subtitle2>Comments</Subtitle2>
          <Button
            appearance="subtle"
            size="small"
            aria-label="close"
            icon={<Dismiss24Regular />}
            onClick={handleCloseSidebar}
          />
        </div>
        <div className="flex justify-between items-center">
          <Button appearance="secondary" icon={<CommentAddRegular />} onClick={handleNewCommentClick}>
            New
          </Button>
          <ResolutionTabs
            resolutionTabs={COMMENT_RESOLUTION_TABS}
            selectedResolutionTab={selectedResolutionTab}
            setSelectedResolutionTab={setSelectedResolutionTab}
          />
        </div>
      </div>
      <CustomScrollbar className="w-full overflow-x-hidden" ref={commentContainerRef as any}>
        <div className="px-4 pb-4 w-full flex flex-col gap-4">
          {isNewCommentOpen && (
            <NewComment item={item} versionId={versionId} setIsNewCommentOpen={setIsNewCommentOpen} />
          )}
          {filteredAndSortedComments.map((comment) => (
            <SingleComment
              key={comment._id}
              comment={comment}
              item={item}
              version={version}
              permissions={permissions}
              versionId={versionId}
              resolvedItems={resolvedItems}
            />
          ))}
        </div>
      </CustomScrollbar>
    </div>
  );
};

export default AllComments;
